/**
 * `yearMonth` widget ("YYYY-MM"): a month menu and a year field, plus the
 * null label ("Present") for open-ended dates. The Inspector pairs two of
 * these into a date range when a prop declares `pairWith`. Mixed, both parts
 * show a dash, and editing one fills the other from the current month.
 */
import { useState } from "react";
import type { PropType } from "../../world/schema/propTypes";
import { FieldError, fieldAria } from "./fieldAria";
import { indeterminateRef, isMixed, MIXED_LABEL, MIXED_OPTION, MixedOption } from "./mixed";
import type { WidgetProps } from "./types";

type YearMonthProp = Extract<PropType, { type: "yearMonth" }>;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function YearMonthWidget({
  id,
  label,
  prop,
  value,
  onChange,
  onCommit,
  error,
}: WidgetProps) {
  const date = prop as YearMonthProp;
  const open = value === null;
  const mixed = isMixed(value);
  const [year = "", month = "01"] = (typeof value === "string" ? value : thisMonth()).split("-");
  const [yearDraft, setYearDraft] = useState<string | null>(null);
  const disabled = prop.readonly === true;

  return (
    <span className="kn-field">
      <span className="kn-yearmonth">
        {/* An open-ended date shows only its label ("Sep 2025 → Present"). */}
        {!open && (
          <>
            <select
              id={id}
              className="kn-input kn-select kn-yearmonth__month"
              aria-label={`${label} month`}
              value={mixed ? MIXED_OPTION : month}
              disabled={disabled}
              {...fieldAria(id, error)}
              onChange={(event) => {
                onChange(`${year}-${event.target.value}`);
                onCommit();
              }}
            >
              <MixedOption mixed={mixed} />
              {MONTHS.map((name, i) => {
                const number = String(i + 1).padStart(2, "0");
                return (
                  <option key={number} value={number}>
                    {name}
                  </option>
                );
              })}
            </select>
            <input
              type="text"
              inputMode="numeric"
              className="kn-input kn-yearmonth__year"
              aria-label={`${label} year`}
              value={yearDraft ?? (mixed ? "" : year)}
              {...(mixed && { placeholder: MIXED_LABEL })}
              disabled={disabled}
              onChange={(event) => {
                setYearDraft(event.target.value);
                if (/^\d{4}$/.test(event.target.value)) onChange(`${event.target.value}-${month}`);
              }}
              onBlur={() => {
                setYearDraft(null);
                onCommit();
              }}
            />
          </>
        )}
        {date.nullable && (
          <label className="kn-yearmonth__open">
            <input
              // When open-ended, this is the field's only control, so it takes the row label's id.
              {...(open && { id })}
              ref={indeterminateRef(mixed)}
              type="checkbox"
              className="kn-checkbox"
              checked={open}
              disabled={disabled}
              onChange={() => {
                onChange(open ? thisMonth() : null);
                onCommit();
              }}
            />
            {date.nullLabel ?? "None"}
          </label>
        )}
      </span>
      <FieldError id={id} error={error} />
    </span>
  );
}

/** Where a newly closed date range starts: the current month. */
function thisMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}
